<script setup>
  import { computed, onMounted } from "vue";
  import { storeToRefs } from "pinia";
  import { Select } from "@/components/episto-ui/select";
  import { LabeledField } from "@/components/episto-ui/labeled-field";
  import { useAdsPagesStore } from "@stores/adsPages";
  import { useMCampaignStore } from "@stores/mCampaign";

  // Sentinel for the "Auto" option — facebook external_ids are numeric strings,
  // so a __-prefixed value can never collide.
  const AUTO_VALUE = "__auto__";

  const props = defineProps({
    modelValue: {
      type: String,
      default: null,
    },
  });

  const emit = defineEmits(["update:modelValue"]);

  const adsPagesStore = useAdsPagesStore();
  const { adsPages, facebookPageOptions } = storeToRefs(adsPagesStore);
  const { fetchAdsPages } = adsPagesStore;

  const mCampaignStore = useMCampaignStore();
  const { currentMCampaign } = storeToRefs(mCampaignStore);

  onMounted(async () => {
    if (adsPages.value.length === 0) await fetchAdsPages();
  });

  // Resolve the master campaign's facebook page name to show it inline
  // in the Auto option label. Falls back to a generic hint when:
  //   - the master has no facebook page set,
  //   - pages haven't loaded yet,
  //   - the external_id doesn't match any known page.
  const autoLabel = computed(() => {
    const externalId = currentMCampaign.value?.pages?.facebook;
    if (!externalId) return "Auto (use master campaign page)";
    const page = adsPages.value.find((p) => p.external_id === externalId);
    return page?.name ? `Auto (${page.name})` : "Auto (use master campaign page)";
  });

  const optionsWithAuto = computed(() => [
    { value: AUTO_VALUE, label: autoLabel.value },
    ...facebookPageOptions.value,
  ]);

  // null on the wire ↔ AUTO_VALUE in the Select UI.
  const internalValue = computed(() => props.modelValue || AUTO_VALUE);

  const handleUpdate = (value) => {
    emit("update:modelValue", value === AUTO_VALUE ? null : value);
  };
</script>

<template>
  <LabeledField label="Facebook page">
    <Select
      :model-value="internalValue"
      :options="optionsWithAuto"
      filterable
      @update:model-value="handleUpdate" />
  </LabeledField>
</template>
